import { useState } from "react";
import { Search, Users, Mail, Phone, Calendar, Loader2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import { useBusiness } from "@/hooks/use-business";
import { useCustomers } from "@/hooks/use-customers";
import { format } from "date-fns";
import { formatPhoneForDisplay } from "@/lib/phone-utils";
import { CustomerOverviewCards } from "@/components/customers/CustomerOverviewCards";

export default function Customers() {
  const { business, loading: businessLoading } = useBusiness();
  const { customers, loading: customersLoading, statLabel } = useCustomers();
  const [searchQuery, setSearchQuery] = useState("");
  
  // Check if this is a clinic
  const isClinic = (business as any)?.primary_module === "clinics";
  const customerLabel = isClinic ? "Patients" : "Customers";

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
      (customer.phone?.includes(searchQuery) ?? false)
  );

  const isLoading = businessLoading || customersLoading;

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title={customerLabel}
        description={`View and manage your ${customerLabel.toLowerCase()} database`}
      />

      <CustomerOverviewCards
        customers={customers}
        loading={isLoading}
        statLabel={statLabel}
        customerLabel={customerLabel}
      />

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${customerLabel.toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Customers Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredCustomers.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.map((customer) => (
            <div
              key={customer.id}
              className="bg-card rounded-xl border border-border shadow-soft p-5 hover:shadow-soft-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-lg">
                  {customer.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground truncate">
                    {customer.name}
                  </h3>
                  <div className="mt-2 space-y-1">
                    {customer.email && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                        <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{customer.email}</span>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                        <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{formatPhoneForDisplay(customer.phone)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>
                    {customer.totalAppointments} {statLabel}
                  </span>
                </div>
                {customer.lastVisit && (
                  <span className="text-muted-foreground">
                    Last: {format(new Date(customer.lastVisit), "MMM d, yyyy")}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border shadow-soft">
          <EmptyState
            icon={Users}
            title="No customers yet"
            description={
              statLabel === "visits"
                ? "Customers will appear here once they check in at the Access Desk"
                : statLabel === "orders"
                  ? "Customers will appear here once they place orders"
                : "Customers will appear here once they book appointments"
            }
          />
        </div>
      )}
    </AppShell>
  );
}
